import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketWatchWidget } from '../components/dashboard/MarketWatchWidget';
import { StatsGrid } from '../components/dashboard/StatsGrid';
import { EnrolledCoursesCard } from '../components/dashboard/EnrolledCoursesCard';
import { UpcomingSessionCard } from '../components/dashboard/UpcomingSessionCard';

export default function DashboardOverview() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [courses, setCourses] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [marketData, setMarketData] = useState([]);
  const [marketSource, setMarketSource] = useState('');
  const [loading, setLoading] = useState(true);
  const [marketLoading, setMarketLoading] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [coursesRes, bookingsRes, marketRes] = await Promise.all([
        api.get('/api/my-courses'),
        api.get('/api/consulting/my-bookings'),
        api.get('/api/market/ticker')
      ]);
      setCourses(coursesRes.data);
      setBookings(bookingsRes.data);
      setMarketData(marketRes.data.data);
      setMarketSource(marketRes.data.source);
    } catch (error) {
      // Dashboard data fetch failed
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const refreshMarketData = async () => {
    setMarketLoading(true);
    try {
      const response = await api.get('/api/market/ticker');
      setMarketData(response.data.data);
      setMarketSource(response.data.source);
    } catch (error) {
      // Market refresh failed
    } finally {
      setMarketLoading(false);
    }
  };

  return (
    <div className="p-8" data-testid="dashboard-overview">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {t('dashboard.welcomeBack')}, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-[#71717A]">Here's what's happening with your trading journey</p>
        </div>
        <Link to="/">
          <Button className="btn-primary flex items-center gap-2" data-testid="header-back-home">
            <Home className="w-4 h-4" /> {t('nav.backToHome')}
          </Button>
        </Link>
      </div>

      <MarketWatchWidget 
        marketData={marketData} 
        marketSource={marketSource} 
        marketLoading={marketLoading} 
        onRefresh={refreshMarketData} 
      />
      <StatsGrid courses={courses} bookings={bookings} userRole={user?.role} />

      <div className="grid lg:grid-cols-2 gap-6">
        <EnrolledCoursesCard courses={courses} loading={loading} />
        <UpcomingSessionCard bookings={bookings} />
      </div>
    </div>
  );
}
